import { ActionIcon, Alert, Box, Button, Card, Divider, Group, Pagination, SimpleGrid, Select, Skeleton, Stack, Table, Text, TextInput, ThemeIcon, Tooltip } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconAlertTriangle, IconArrowBackUp, IconCalendar, IconCashBanknote, IconCoins, IconDownload, IconEye, IconPercentage, IconReceiptOff, IconSearch } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { TenantCollectedFeeApi, TenantFeePolicyAssignmentApi } from "../../apis";
import type { CollectedFee, CollectedFeeStatus } from "../../apis/tenantCollectedFee";
import { CollectedFeeDetailDrawer } from "../../components/billing/CollectedFeeDetailDrawer";
import { CollectedFeeStatusBadge } from "../../components/billing/CollectedFeeStatusBadge";
import { CustomerCell } from "../../components/billing/CustomerCell";
import { EmptyState } from "../../components/billing/EmptyState";
import { MoneyCell } from "../../components/billing/MoneyCell";
import { OrderRefCell, orderCodeOf } from "../../components/billing/OrderRefCell";
import { formatDate, formatMoney, formatPercent } from "../../utils/format";

const footerBar = { background: "light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))" };
const PAGE_SIZE = 20;

type KpiTone = "neutral" | "red" | "green";

const KPI_TONE: Record<KpiTone, { value?: string; icon: string }> = {
  neutral: { value: undefined, icon: "gray" },
  red    : { value: "red.6", icon: "red" },
  green  : { value: "green.7", icon: "green" },
};

/** Flat, tone-matched KPI tile: tiny uppercase label, a subtle corner icon in
 *  the tone color, a large tabular value, and a compact sub-line. */
function FeeKpiCard({
  icon, label, value, sub, tone = "neutral",
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub: React.ReactNode;
  tone?: KpiTone;
}) {
  const t = KPI_TONE[tone];
  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" align="center" wrap="nowrap" mb="lg">
        <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: "0.06em" }}>
          {label}
        </Text>
        <ThemeIcon variant="light" color={t.icon} radius="md" size={34}>
          {icon}
        </ThemeIcon>
      </Group>
      <Text fz={30} fw={700} lh={1.1} c={t.value} style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Text>
      <Text size="xs" c="dimmed" mt={8}>{sub}</Text>
    </Card>
  );
}

const MyCollectedFees = () => {
  const [status, setStatus] = useState<CollectedFeeStatus | "">("");
  const [orderNumber, setOrderNumber] = useState("");
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [selected, setSelected] = useState<CollectedFee | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fromDate = dateRange[0] || undefined;
  const toDate = dateRange[1] ? `${dateRange[1]}T23:59:59.999` : undefined;

  const { data, error, isLoading } = useSWR(
    `swr.my-billing.collected-fees.${status}.${orderNumber}.${fromDate ?? ""}.${toDate ?? ""}`,
    () =>
      TenantCollectedFeeApi.list({
        status: status || undefined,
        orderNumber: orderNumber || undefined,
        fromDate,
        toDate,
        page: 1, limit: 200
      })
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any;
  const rows: CollectedFee[] = Array.isArray(raw) ? raw : (raw?.rows ?? []);
  // Server-reported total; when it exceeds the loaded window we say so rather
  // than silently capping.
  const totalRecords: number = Array.isArray(raw) ? raw.length : (raw?.total ?? rows.length);
  const capped = totalRecords > rows.length;

  // Resolve end-customer business ids -> names via the billing roster (same source
  // the Customers tab uses). PartnerApi can't: partner.id != business.id.
  const { data: roster } = useSWR("swr.my-billing.customer-roster", () =>
    TenantFeePolicyAssignmentApi.listCustomers({ page: 1, limit: 1000 }));
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    (roster?.rows ?? []).forEach((cst) => { if (cst.name) m.set(cst.businessId, cst.name); });
    return m;
  }, [roster]);
  const customerName = (id: string) => nameById.get(id) ?? id.slice(0, 8) + "…";

  // Client-side pagination of the loaded (filtered) rows; KPIs/CSV still use the
  // full set. Reset to page 1 whenever the filters change.
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [status, orderNumber, fromDate, toDate]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const collectedTotal = rows
    .filter((r) => r.status !== "REFUNDED")
    .reduce((sum, r) => sum + Number(r.finalFeeAmount || 0), 0);
  const refundedTotal = rows
    .filter((r) => r.status === "REFUNDED")
    .reduce((sum, r) => sum + Number(r.finalFeeAmount || 0), 0);
  const anyMinApplied = rows.some((r) => r.minApplied);

  // KPI summary over the currently loaded (filtered) rows — same row-based basis
  // the footer used, surfaced as the four tiles above the list.
  const feeCurrency = rows[0]?.feeCurrency;
  const collectedCount = rows.filter((r) => r.status !== "REFUNDED").length;
  const refundedCount = rows.filter((r) => r.status === "REFUNDED").length;
  const netTotal = collectedTotal - refundedTotal;
  const ratedRows = rows.filter((r) => r.appliedRate != null && r.appliedRate !== "");
  const avgRatePct = ratedRows.length
    ? (ratedRows.reduce((sum, r) => sum + Number(r.appliedRate), 0) / ratedRows.length) * 100
    : 0;

  const STATUS_LABEL: Record<CollectedFeeStatus, string> = {
    PENDING : "Хүлээгдэж буй",
    DEDUCTED: "Хассан",
    INVOICED: "Нэхэмжилсэн",
    SETTLED : "Төлсөн",
    WAIVED  : "Хүчингүйд",
    FAILED  : "Алдсан",
    REFUNDED: "Буцаасан"
  };

  const handleExportCsv = () => {
    const headers = ["Захиалга №", "Огноо", "Үндсэн дүн", "Хувь", "Шимтгэл", "Төлөв"];
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const rate = (r: CollectedFee) =>
      r.appliedRate != null && r.appliedRate !== "" ? `${(Number(r.appliedRate) * 100).toFixed(2)}%` : "";
    const lines = rows.map((r) =>
      [
        orderCodeOf(r) ?? r.orderId,
        formatDate(r.collectedAt),
        formatMoney(Number(r.baseAmount || 0), r.baseCurrency),
        rate(r),
        formatMoney(Number(r.finalFeeAmount || 0), r.feeCurrency),
        STATUS_LABEL[r.status] ?? r.status
      ]
        .map((c) => escape(String(c)))
        .join(",")
    );
    const csv = "﻿" + [headers.map(escape).join(","), ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `collected-fees-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box pt="md">
      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" mb="md">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={116} radius="md" />
          ))}
        </SimpleGrid>
      ) : !error && rows.length > 0 ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" mb="md">
          <FeeKpiCard
            icon={<IconCoins size={20} />}
            label="Цуглуулсан шимтгэл"
            value={formatMoney(collectedTotal, feeCurrency)}
            sub={`${collectedCount} гүйлгээ`}
          />
          <FeeKpiCard
            icon={<IconArrowBackUp size={20} />}
            label="Буцаасан"
            tone="red"
            value={formatMoney(refundedTotal, feeCurrency)}
            sub={`${refundedCount} буцаалт`}
          />
          <FeeKpiCard
            icon={<IconCashBanknote size={20} />}
            label="Цэвэр шимтгэл"
            tone="green"
            value={formatMoney(netTotal, feeCurrency)}
            sub="цуглуулсан − буцаасан"
          />
          <FeeKpiCard
            icon={<IconPercentage size={20} />}
            label="Дундаж хувь"
            value={formatPercent(avgRatePct)}
            sub="бүх бодлогын дундаж"
          />
        </SimpleGrid>
      ) : null}

      <Card withBorder radius="md" padding="md">
      <Stack gap="md">
        <Group gap="sm">
          <DatePickerInput
            type="range"
            placeholder="Огнооны хязгаар"
            valueFormat="YYYY-MM-DD"
            value={dateRange}
            onChange={setDateRange}
            leftSection={<IconCalendar size={16} />}
            clearable
            w={260}
          />
          <Select
            placeholder="Бүх төлөв"
            data={[
              { value: "PENDING", label: "Хүлээгдэж буй" },
              { value: "DEDUCTED", label: "Хассан" },
              { value: "INVOICED", label: "Нэхэмжилсэн" },
              { value: "SETTLED", label: "Төлсөн" },
              { value: "WAIVED", label: "Хүчингүйд" },
              { value: "FAILED", label: "Алдсан" },
              { value: "REFUNDED", label: "Буцаасан" }
            ]}
            value={status || null}
            onChange={(v) => setStatus((v as CollectedFeeStatus) || "")}
            clearable w={200}
          />
          <TextInput
            placeholder="Захиалга №-ээр хайх"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            w={250}
          />
          <Button
            variant="default"
            leftSection={<IconDownload size={16} />}
            onClick={handleExportCsv}
            disabled={rows.length === 0}
            ml="auto"
          >
            CSV татах
          </Button>
        </Group>

        {error ? (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>Шимтгэл ачааллахад алдаа гарлаа.</Alert>
        ) : isLoading ? (
          <Skeleton height={240} radius="md" />
        ) : rows.length === 0 ? (
          <EmptyState icon={<IconReceiptOff size={26} />} message="Цуглуулсан шимтгэл алга байна." />
        ) : (
          <>
            <Divider />
            <Table.ScrollContainer minWidth={760}>
              <Table verticalSpacing="sm" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={110}>Огноо</Table.Th><Table.Th w={150}>Захиалга №</Table.Th><Table.Th>Харилцагч</Table.Th>
                    <Table.Th ta="right" w={140}>Үндсэн дүн</Table.Th><Table.Th ta="right" w={90}>Хувь</Table.Th>
                    <Table.Th ta="right" w={150}>Шимтгэл</Table.Th><Table.Th w={130}>Төлөв</Table.Th>
                    <Table.Th w={48}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pagedRows.map((r) => (
                    <Table.Tr key={r.id}>
                      <Table.Td><Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{formatDate(r.collectedAt)}</Text></Table.Td>
                      <Table.Td><OrderRefCell orderNumber={r.orderNumber} orderId={r.orderId} /></Table.Td>
                      <Table.Td><CustomerCell name={customerName(r.endCustomerBusinessId)} /></Table.Td>
                      <Table.Td ta="right"><MoneyCell value={r.baseAmount} currency={r.baseCurrency} /></Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>
                          {r.appliedRate != null && r.appliedRate !== "" ? formatPercent(Number(r.appliedRate) * 100) : "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          <MoneyCell value={r.finalFeeAmount} currency={r.feeCurrency} fw={500} />
                          {r.minApplied && (
                            <Tooltip label="Минимум хязгаар хэрэглэсэн">
                              <Text span c="orange" fw={700} style={{ cursor: "default" }}>*</Text>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td><CollectedFeeStatusBadge status={r.status} /></Table.Td>
                      <Table.Td>
                        <Tooltip label="Дэлгэрэнгүй">
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={() => { setSelected(r); setDrawerOpen(true); }}
                            aria-label="Дэлгэрэнгүй харах"
                          >
                            <IconEye size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <Group justify="space-between" wrap="wrap" gap="md" p="sm" style={{ ...footerBar, borderRadius: "var(--mantine-radius-md)" }}>
              <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                {(safePage - 1) * PAGE_SIZE + 1}-{Math.min(safePage * PAGE_SIZE, rows.length)} / {rows.length} бичлэг
                {capped ? ` (нийт ${totalRecords}, эхний ${rows.length}-г харуулж байна)` : ""}
                {anyMinApplied ? " · * Минимум хязгаар хэрэглэсэн" : ""}
              </Text>
              {pageCount > 1 && (
                <Pagination value={safePage} onChange={setPage} total={pageCount} size="sm" />
              )}
            </Group>
          </>
        )}
      </Stack>
      </Card>

      <CollectedFeeDetailDrawer
        fee={selected}
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </Box>
  );
};

export default MyCollectedFees;
